import apiClient from "@/api/client";
import type {
  ClientBranch,
  ClientSubBranch,
  MarkTenderWonFormData,
  MyPricingNotification,
  PricingItemTemplate,
  PricingItemTemplateFormData,
  PricingPreset,
  PricingPresetFormData,
  PricingAdvicePayload,
  PricingAdviceResponse,
  PricingModelResponse,
  PricingPresetFunction,
  PricingPresetFunctionFormData,
  ResolvedPresetItem,
  SimilarProposal,
  Tender,
  TenderFormData,
  TenderListItem,
  TenderOffer,
  TenderOfferFormData,
  TenderPricingProposal,
  TenderPricingProposalFormData,
  TenderRound,
  TenderScopePrefix,
  TenderScopePrefixFormData,
} from "../types";


interface ApiResponse<T> {
  data: T;
  errors: string | null;
  meta: Record<string, unknown>;
}

export interface TenderClientOption {
  id: string;
  name: string;
  short_name: string;
  nip: string;
}

export async function fetchTenderClients(): Promise<TenderClientOption[]> {
  const res = await apiClient.get<ApiResponse<TenderClientOption[]>>("/tenders-clients/");
  return res.data.data;
}

export async function fetchClientBranches(clientId: string): Promise<ClientBranch[]> {
  const res = await apiClient.get<ApiResponse<ClientBranch[]>>("/client-branches/", {
    params: { client: clientId, active: "true" },
  });
  return res.data.data;
}

export async function createClientBranch(data: {
  client: string;
  name: string;
  street?: string;
  postal_code?: string;
  city?: string;
  contact_person?: string;
  contact_email?: string;
  contact_phone?: string;
  notes?: string;
}): Promise<ClientBranch> {
  const res = await apiClient.post<ApiResponse<ClientBranch>>("/client-branches/", data);
  return res.data.data;
}

export async function fetchClientSubBranches(branchId: string): Promise<ClientSubBranch[]> {
  const res = await apiClient.get<ApiResponse<ClientSubBranch[]>>("/client-sub-branches/", {
    params: { branch: branchId, active: "true" },
  });
  return res.data.data;
}

export async function createClientSubBranch(data: {
  branch: string;
  name: string;
  postal_code?: string;
  city?: string;
  contact_person?: string;
  contact_email?: string;
  contact_phone?: string;
  notes?: string;
}): Promise<ClientSubBranch> {
  const res = await apiClient.post<ApiResponse<ClientSubBranch>>("/client-sub-branches/", data);
  return res.data.data;
}

export async function fetchSimilarProposals(
  tenderId: string,
  templateIds: string[]
): Promise<SimilarProposal[]> {
  const params: Record<string, string> = {};
  if (templateIds.length > 0) params.template_ids = templateIds.join(",");
  const res = await apiClient.get<ApiResponse<SimilarProposal[]>>(
    `/tenders/${tenderId}/similar-proposals/`,
    { params }
  );
  return res.data.data;
}

export async function fetchPricingModel(tenderId: string): Promise<PricingModelResponse> {
  const res = await apiClient.post<ApiResponse<PricingModelResponse>>(
    "/power-projects/ze-analytics/pricing-model/",
    { tender_id: tenderId }
  );
  return res.data.data;
}

export async function fetchPricingAdvice(
  payload: PricingAdvicePayload
): Promise<PricingAdviceResponse> {
  const res = await apiClient.post<ApiResponse<PricingAdviceResponse>>(
    "/power-projects/ze-analytics/pricing-advice/",
    payload
  );
  return res.data.data;
}

export async function fetchTenders(params?: { status?: string; client?: string }): Promise<TenderListItem[]> {
  // ?omit=description — the list is unpaginated and TenderListItem has no `description`,
  // so shipping the full tender description per row was pure payload. A deny-list param
  // keeps the field for other API clients (ARON, the browser extension) that read it.
  const res = await apiClient.get<ApiResponse<TenderListItem[]>>("/tenders/", {
    params: { ...params, omit: "description" },
  });
  return res.data.data;
}

export async function fetchTender(id: string): Promise<Tender> {
  const res = await apiClient.get<ApiResponse<Tender>>(`/tenders/${id}/`);
  return res.data.data;
}

export async function createTender(data: TenderFormData): Promise<Tender> {
  const res = await apiClient.post<ApiResponse<Tender>>("/tenders/", data);
  return res.data.data;
}

export async function updateTender(id: string, data: Partial<TenderFormData>): Promise<Tender> {
  const res = await apiClient.patch<ApiResponse<Tender>>(`/tenders/${id}/`, data);
  return res.data.data;
}

export async function deleteTender(id: string): Promise<void> {
  await apiClient.delete(`/tenders/${id}/`);
}

export async function setTenderStatus(id: string, status: "open" | "won_unofficial" | "lost" | "cancelled" | "not_starting"): Promise<Tender> {
  const res = await apiClient.patch<ApiResponse<Tender>>(`/tenders/${id}/set-status/`, { status });
  return res.data.data;
}

// Rounds
export async function addTenderRound(tenderId: string, data: { offer_deadline?: string; notes?: string }): Promise<TenderRound> {
  const res = await apiClient.post<ApiResponse<TenderRound>>(`/tenders/${tenderId}/add-round/`, data);
  return res.data.data;
}

export async function updateTenderRound(tenderId: string, roundId: string, data: { offer_deadline?: string; status?: string; notes?: string }): Promise<TenderRound> {
  const res = await apiClient.patch<ApiResponse<TenderRound>>(`/tenders/${tenderId}/rounds/${roundId}/`, data);
  return res.data.data;
}

// Attachments
export async function uploadTenderAttachment(tenderId: string, file: File, name: string): Promise<void> {
  const form = new FormData();
  form.append("file", file);
  form.append("name", name);
  await apiClient.post(`/tenders/${tenderId}/attachments/`, form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
}

export async function deleteTenderAttachment(tenderId: string, attachmentId: string): Promise<void> {
  await apiClient.delete(`/tenders/${tenderId}/attachments/${attachmentId}/`);
}

// Material files
export async function uploadTenderMaterial(tenderId: string, file: File, name: string): Promise<void> {
  const form = new FormData();
  form.append("file", file);
  form.append("name", name);
  await apiClient.post(`/tenders/${tenderId}/materials/`, form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
}

export async function deleteTenderMaterial(tenderId: string, materialId: string): Promise<void> {
  await apiClient.delete(`/tenders/${tenderId}/materials/${materialId}/`);
}

export async function fetchPendingApprovalsCount(): Promise<number> {
  const res = await apiClient.get<ApiResponse<{ count: number }>>("/tenders/pending-approvals-count/");
  return res.data.data.count;
}

export interface TenderActionCounts {
  needs_pricing: number;
  pricing_claimed: number;
  needs_approval: number;
  needs_offer: number;
  offer_submitted: number;
  needs_result: number;
}

export async function fetchTenderActionCounts(): Promise<TenderActionCounts> {
  const res = await apiClient.get<ApiResponse<TenderActionCounts>>("/tenders/action-counts/");
  return res.data.data;
}

// Watch / unwatch
export async function watchTender(tenderId: string): Promise<void> {
  await apiClient.post(`/tenders/${tenderId}/watch/`);
}

export async function unwatchTender(tenderId: string): Promise<void> {
  await apiClient.post(`/tenders/${tenderId}/unwatch/`);
}

// Przejęcie wyceny — „Przejęte (Imię Nazwisko)" zamiast „Czeka na wyliczenie wyceny".
// Backend odpowiada 409, gdy turę przejął już ktoś inny; { force: true } przejmuje mimo to.
export async function claimTenderPricing(tenderId: string, force = false): Promise<Tender> {
  const res = await apiClient.post<ApiResponse<Tender>>(
    `/tenders/${tenderId}/claim-pricing/`,
    force ? { force: true } : {},
  );
  return res.data.data;
}

export async function releaseTenderPricing(tenderId: string): Promise<Tender> {
  const res = await apiClient.post<ApiResponse<Tender>>(`/tenders/${tenderId}/release-pricing/`);
  return res.data.data;
}

// Messages
export async function postTenderMessage(
  tenderId: string,
  data: { content: string; parent?: string; files?: File[]; mentionIds?: string[] }
): Promise<void> {
  const form = new FormData();
  form.append("content", data.content);
  if (data.parent) form.append("parent", data.parent);
  if (data.files) {
    data.files.forEach((f) => form.append("files", f));
  }
  if (data.mentionIds) {
    data.mentionIds.forEach((id) => form.append("mention_ids", id));
  }
  await apiClient.post(`/tenders/${tenderId}/messages/`, form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
}

// Upload a single image and return its URL, for inline embedding in a message.
export async function uploadTenderMessageImage(tenderId: string, file: File): Promise<string> {
  const form = new FormData();
  form.append("file", file);
  const res = await apiClient.post<{ data: { url: string } }>(
    `/tenders/${tenderId}/messages/upload-image/`,
    form,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
  return res.data.data.url;
}

export async function markTenderMessage(
  tenderId: string,
  messageId: string,
  is_marked: boolean,
  mark_type?: string | null
): Promise<void> {
  await apiClient.patch(`/tenders/${tenderId}/messages/${messageId}/mark/`, { is_marked, mark_type });
}

export async function deleteTenderMessage(tenderId: string, messageId: string): Promise<void> {
  await apiClient.delete(`/tenders/${tenderId}/messages/${messageId}/`);
}

// Offers
export async function submitTenderOffer(tenderId: string, roundId: string, data: TenderOfferFormData): Promise<TenderOffer> {
  const form = new FormData();
  form.append("value", data.value);
  if (data.notes) form.append("notes", data.notes);
  if (data.file) form.append("file", data.file);
  const res = await apiClient.post<ApiResponse<TenderOffer>>(
    `/tenders/${tenderId}/rounds/${roundId}/offers/`,
    form,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
  return res.data.data;
}

export async function withdrawTenderOffer(tenderId: string, roundId: string, offerId: string): Promise<TenderOffer> {
  const res = await apiClient.post<ApiResponse<TenderOffer>>(
    `/tenders/${tenderId}/rounds/${roundId}/offers/${offerId}/withdraw/`
  );
  return res.data.data;
}

// Pricing proposals
export async function createPricingProposal(
  tenderId: string,
  roundId: string,
  data: TenderPricingProposalFormData
): Promise<TenderPricingProposal> {
  const res = await apiClient.post<ApiResponse<TenderPricingProposal>>(
    `/tenders/${tenderId}/rounds/${roundId}/pricing-proposals/`,
    data
  );
  return res.data.data;
}

export async function sendPricingNotification(
  tenderId: string,
  roundId: string,
  proposalId: string,
  recipient_ids: string[],
  message: string
): Promise<void> {
  await apiClient.post(
    `/tenders/${tenderId}/rounds/${roundId}/pricing-proposals/${proposalId}/notify/`,
    { recipient_ids, message }
  );
}

export async function updatePricingProposal(
  tenderId: string,
  roundId: string,
  proposalId: string,
  data: TenderPricingProposalFormData
): Promise<TenderPricingProposal> {
  const res = await apiClient.patch<ApiResponse<TenderPricingProposal>>(
    `/tenders/${tenderId}/rounds/${roundId}/pricing-proposals/${proposalId}/`,
    data
  );
  return res.data.data;
}

export async function reviewPricingProposal(
  tenderId: string,
  roundId: string,
  proposalId: string,
  action: "accept" | "reject"
): Promise<TenderPricingProposal> {
  const res = await apiClient.post<ApiResponse<TenderPricingProposal>>(
    `/tenders/${tenderId}/rounds/${roundId}/pricing-proposals/${proposalId}/review/`,
    { action }
  );
  return res.data.data;
}

// Mark as won
export async function markTenderWon(tenderId: string, data: MarkTenderWonFormData): Promise<Tender> {
  const form = new FormData();
  form.append("coordinator_id", data.coordinator_id);
  form.append("project_name", data.project_name);
  form.append("project_short_name", data.project_short_name);
  if (data.planned_revenue != null) form.append("planned_revenue", data.planned_revenue);
  if (data.planned_revenue_date != null) form.append("planned_revenue_date", data.planned_revenue_date);
  if (data.branch_name != null) form.append("branch_name", data.branch_name);
  if (data.sub_branch_name != null) form.append("sub_branch_name", data.sub_branch_name);
  if (data.contract_file) form.append("contract_file", data.contract_file);
  const res = await apiClient.post<ApiResponse<Tender>>(
    `/tenders/${tenderId}/mark-won/`,
    form,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
  return res.data.data;
}

// Pricing notifications for current user
export async function fetchMyPricingNotifications(unread?: boolean): Promise<MyPricingNotification[]> {
  const res = await apiClient.get<ApiResponse<MyPricingNotification[]>>(
    "/tenders/pricing-notifications/",
    { params: unread ? { unread: "true" } : undefined }
  );
  return res.data.data;
}

export async function markPricingNotificationRead(id: string): Promise<void> {
  await apiClient.post(`/tenders/pricing-notifications/${id}/mark-read/`);
}

export async function togglePricingNotificationPin(id: string): Promise<{ id: string; is_pinned: boolean }> {
  const res = await apiClient.patch<{ data: { id: string; is_pinned: boolean } }>(`/tenders/pricing-notifications/${id}/pin/`);
  return res.data.data;
}

// ── Pricing catalog — templates ───────────────────────────────────────────

export async function fetchPricingTemplates(params?: {
  client?: string;
  active?: string;
}): Promise<PricingItemTemplate[]> {
  const res = await apiClient.get<ApiResponse<PricingItemTemplate[]>>(
    "/tenders/pricing-templates/",
    { params }
  );
  return res.data.data;
}

export async function createPricingTemplate(
  data: PricingItemTemplateFormData
): Promise<PricingItemTemplate> {
  const res = await apiClient.post<ApiResponse<PricingItemTemplate>>(
    "/tenders/pricing-templates/",
    data
  );
  return res.data.data;
}

export async function updatePricingTemplate(
  id: string,
  data: Partial<PricingItemTemplateFormData>
): Promise<PricingItemTemplate> {
  const res = await apiClient.patch<ApiResponse<PricingItemTemplate>>(
    `/tenders/pricing-templates/${id}/`,
    data
  );
  return res.data.data;
}

export async function deletePricingTemplate(id: string): Promise<void> {
  await apiClient.delete(`/tenders/pricing-templates/${id}/`);
}

// ── Pricing catalog — presets ─────────────────────────────────────────────

export async function fetchPricingPresets(params?: {
  client?: string;
}): Promise<PricingPreset[]> {
  const res = await apiClient.get<ApiResponse<PricingPreset[]>>(
    "/tenders/pricing-presets/",
    { params }
  );
  return res.data.data;
}

export async function createPricingPreset(
  data: PricingPresetFormData
): Promise<PricingPreset> {
  const res = await apiClient.post<ApiResponse<PricingPreset>>(
    "/tenders/pricing-presets/",
    data
  );
  return res.data.data;
}

export async function updatePricingPreset(
  id: string,
  data: PricingPresetFormData
): Promise<PricingPreset> {
  const res = await apiClient.patch<ApiResponse<PricingPreset>>(
    `/tenders/pricing-presets/${id}/`,
    data
  );
  return res.data.data;
}

export async function deletePricingPreset(id: string): Promise<void> {
  await apiClient.delete(`/tenders/pricing-presets/${id}/`);
}

export async function resolvePricingPreset(
  id: string,
  parameters: Record<string, number>
): Promise<ResolvedPresetItem[]> {
  const res = await apiClient.post<ApiResponse<ResolvedPresetItem[]>>(
    `/tenders/pricing-presets/${id}/resolve/`,
    { parameters }
  );
  return res.data.data;
}

// ── Pricing catalog — preset functions (global Python quantity functions) ────

export async function fetchPricingFunctions(params?: {
  search?: string;
  status?: string;
}): Promise<PricingPresetFunction[]> {
  const res = await apiClient.get<ApiResponse<PricingPresetFunction[]>>(
    "/tenders/pricing-functions/",
    { params }
  );
  return res.data.data;
}

export async function createPricingFunction(
  data: PricingPresetFunctionFormData
): Promise<PricingPresetFunction> {
  const res = await apiClient.post<ApiResponse<PricingPresetFunction>>(
    "/tenders/pricing-functions/",
    data
  );
  return res.data.data;
}

export async function updatePricingFunction(
  id: string,
  data: Partial<PricingPresetFunctionFormData>
): Promise<PricingPresetFunction> {
  const res = await apiClient.patch<ApiResponse<PricingPresetFunction>>(
    `/tenders/pricing-functions/${id}/`,
    data
  );
  return res.data.data;
}

export async function deletePricingFunction(id: string): Promise<void> {
  await apiClient.delete(`/tenders/pricing-functions/${id}/`);
}

async function reviewPricingFunction(
  id: string,
  action: "approve" | "reject" | "revoke"
): Promise<PricingPresetFunction> {
  const res = await apiClient.post<ApiResponse<PricingPresetFunction>>(
    `/tenders/pricing-functions/${id}/${action}/`
  );
  return res.data.data;
}

export const approvePricingFunction = (id: string) => reviewPricingFunction(id, "approve");
export const rejectPricingFunction = (id: string) => reviewPricingFunction(id, "reject");
export const revokePricingFunction = (id: string) => reviewPricingFunction(id, "revoke");

// ── Zakres przetargu — mapowanie prefiksów nazw ──────────────────────────────

export async function fetchScopePrefixes(params?: {
  active?: string;
}): Promise<TenderScopePrefix[]> {
  const res = await apiClient.get<ApiResponse<TenderScopePrefix[]>>(
    "/tenders/scope-prefixes/",
    { params }
  );
  return res.data.data;
}

export async function createScopePrefix(
  data: TenderScopePrefixFormData
): Promise<TenderScopePrefix> {
  const res = await apiClient.post<ApiResponse<TenderScopePrefix>>(
    "/tenders/scope-prefixes/",
    data
  );
  return res.data.data;
}

export async function updateScopePrefix(
  id: string,
  data: Partial<TenderScopePrefixFormData>
): Promise<TenderScopePrefix> {
  const res = await apiClient.patch<ApiResponse<TenderScopePrefix>>(
    `/tenders/scope-prefixes/${id}/`,
    data
  );
  return res.data.data;
}

export async function deleteScopePrefix(id: string): Promise<void> {
  await apiClient.delete(`/tenders/scope-prefixes/${id}/`);
}

/** Załóż OSOBNE przedsięwzięcie budowlane dla wygranego przetargu „projekt i budowa”.
 *  Dołącza do tej samej grupy co przedsięwzięcie projektowe. */
export async function startTenderConstruction(
  id: string,
  data: { name?: string; short_name?: string } = {}
): Promise<{ project_id: string; name: string }> {
  const res = await apiClient.post<ApiResponse<{ project_id: string; name: string }>>(
    `/tenders/${id}/start-construction/`,
    data
  );
  return res.data.data;
}
